import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { getBillingStatus } from "../services/billingApi";
import type { BillingStatusResponse } from "../types/billing";
import {
  resolveBillingPlan,
  resolveSimsPerReport,
  isBillingAccessGranted,
  isWeeklyReportQuotaReached,
} from "../types/billing";
import { BILLING_ACCESS_REVOKED_EVENT } from "../utils/billingPaywall";

export interface UseBillingStatusResult {
  /** True while loading or re-fetching */
  isLoading: boolean;
  error: string | null;
  /** Original billing status error (for gate routing). */
  loadError: unknown;
  status: BillingStatusResponse | null;
  isTier1Granted: boolean;
  plan: ReturnType<typeof resolveBillingPlan>;
  simsPerReport: number;
  weeklyReportQuotaReached: boolean;
  refresh: () => Promise<void>;
  /** Poll status a few times (e.g. after Stripe redirect while webhook catches up) */
  refreshWithRetry: (options?: { attempts?: number; delayMs?: number }) => Promise<void>;
}

export function useBillingStatus(): UseBillingStatusResult {
  const { isLoaded, isSignedIn } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [status, setStatus] = useState<BillingStatusResponse | null>(null);
  const [accessRevoked, setAccessRevoked] = useState(false);

  const load = useCallback(async () => {
    if (!isLoaded) {
      return;
    }
    if (!isSignedIn) {
      setIsLoading(false);
      setStatus(null);
      setError(null);
      setLoadError(null);
      setAccessRevoked(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    setLoadError(null);
    try {
      const data = await getBillingStatus();
      setStatus(data);
      setAccessRevoked(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load billing status";
      setError(message);
      setLoadError(e);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRevoked = () => {
      setAccessRevoked(true);
      void (async () => {
        try {
          const data = await getBillingStatus();
          setStatus(data);
          setAccessRevoked(!isBillingAccessGranted(data));
        } catch {
          // Stay revoked until a later refresh succeeds.
        }
      })();
    };
    window.addEventListener(BILLING_ACCESS_REVOKED_EVENT, onRevoked);
    return () => window.removeEventListener(BILLING_ACCESS_REVOKED_EVENT, onRevoked);
  }, []);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const refreshWithRetry = useCallback(async (options?: { attempts?: number; delayMs?: number }) => {
    const attempts = options?.attempts ?? 5;
    const delayMs = options?.delayMs ?? 2000;
    if (!isLoaded || !isSignedIn) return;

    /** Do not toggle global `isLoading` — avoids blanking billing return routes while status is still fetching. */
    for (let i = 0; i < attempts; i++) {
      if (i > 0) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
      setError(null);
      setLoadError(null);
      try {
        const data = await getBillingStatus();
        setStatus(data);
        setAccessRevoked(false);
        if (isBillingAccessGranted(data)) {
          break;
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to load billing status";
        setError(message);
        setLoadError(e);
      }
    }
  }, [isLoaded, isSignedIn]);

  const isTier1Granted = !accessRevoked && isBillingAccessGranted(status);
  const plan = resolveBillingPlan(status);
  const simsPerReport = resolveSimsPerReport(status);
  const weeklyReportQuotaReached = isWeeklyReportQuotaReached(status);

  return {
    isLoading,
    error,
    loadError,
    status,
    isTier1Granted,
    plan,
    simsPerReport,
    weeklyReportQuotaReached,
    refresh,
    refreshWithRetry,
  };
}
